'use client';

import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';

interface Review {
  id: string;
  guestName: string;
  guestAvatar: string;
  guestAvatarAlt: string;
  property: string;
  date: string;
  rating: number;
  comment: string;
  cleanliness: number;
  accuracy: number;
  communication: number;
  location: number;
  value: number;
}

const reviews: Review[] = [
  {
    id: '1',
    guestName: 'Sarah Nakamura',
    guestAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&q=80',
    guestAvatarAlt: 'Sarah Nakamura, smiling woman with dark hair',
    property: 'Lakeside Villa Entebbe',
    date: 'Aug 2026',
    rating: 5,
    comment: 'Absolutely stunning villa! The lake views were breathtaking and the host was incredibly responsive. We will definitely be back.',
    cleanliness: 5, accuracy: 5, communication: 5, location: 5, value: 4,
  },
  {
    id: '2',
    guestName: 'David Mwangi',
    guestAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&q=80',
    guestAvatarAlt: 'David Mwangi, man with glasses',
    property: 'Lakeside Villa Entebbe',
    date: 'Jul 2026',
    rating: 5,
    comment: 'Perfect family getaway. The kids loved the pool and the garden. Everything was exactly as described.',
    cleanliness: 5, accuracy: 5, communication: 4, location: 5, value: 5,
  },
  {
    id: '3',
    guestName: 'Emma Lindqvist',
    guestAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&q=80',
    guestAvatarAlt: 'Emma Lindqvist, blonde woman smiling outdoors',
    property: 'Bwindi Forest Lodge',
    date: 'Jun 2026',
    rating: 5,
    comment: 'Magical experience. Waking up to gorilla sounds in the forest was unforgettable. The lodge is beautifully maintained.',
    cleanliness: 5, accuracy: 5, communication: 5, location: 5, value: 4,
  },
  {
    id: '4',
    guestName: 'James Okonkwo',
    guestAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&q=80',
    guestAvatarAlt: 'James Okonkwo, man in casual attire',
    property: 'Kampala City Apartment',
    date: 'May 2026',
    rating: 4,
    comment: 'Great location in Kololo, very clean and modern. The WiFi was fast which was important for my work trip. Minor issue with hot water but host resolved it quickly.',
    cleanliness: 4, accuracy: 4, communication: 5, location: 5, value: 4,
  },
];

const radarData = [
  { subject: 'Cleanliness', score: 4.9 },
  { subject: 'Accuracy', score: 4.8 },
  { subject: 'Communication', score: 4.9 },
  { subject: 'Location', score: 5.0 },
  { subject: 'Value', score: 4.4 },
];

export default function Ratings() {
  const avgRating = (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1);

  return (
    <div>
      {/* Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Big rating */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col items-center justify-center">
          <div className="text-6xl font-bold text-gray-900">{avgRating}</div>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <svg key={s} className={`w-5 h-5 ${s <= Math.round(Number(avgRating)) ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <div className="text-sm text-gray-500 mt-1">Based on {reviews.length} reviews</div>
          <div className="mt-3 bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">Superhost Status ✓</div>
        </div>

        {/* Category breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-4">Category Scores</h3>
          <div className="space-y-3">
            {radarData.map((r) => (
              <div key={r.subject}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{r.subject}</span>
                  <span className="font-bold text-gray-900">{r.score}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full"
                    style={{ width: `${(r.score / 5) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Radar chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-bold text-gray-900 mb-2">Performance Radar</h3>
          <ResponsiveContainer width="100%" height={180}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#f0f0f0" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#9ca3af' }} />
              <Radar dataKey="score" stroke="#f43f5e" fill="#f43f5e" fillOpacity={0.15} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Review cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <img src={r.guestAvatar} alt={r.guestAvatarAlt} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="font-semibold text-gray-900 text-sm">{r.guestName}</div>
                  <div className="text-xs text-gray-400">{r.date} · {r.property}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <svg key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{r.comment}</p>
            <button className="mt-3 text-xs text-[#4088C6] font-semibold hover:underline">Reply to review</button>
          </div>
        ))}
      </div>
    </div>
  );
}
